import React from "react";
import { Box, Typography, Chip, Tooltip } from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { fechaHora, hora } from "./fechas";

// Los ticks de WhatsApp para lo que mandamos: un tick enviado, dos grises entregado, dos azules
// leído. Una falla va en rojo, con el motivo debajo del mensaje.
const estadoDeEnvio = ({ direction, sent_at, delivered_at, read_at, failed_at }) => {
    if (direction !== "outbound") return null;
    if (failed_at) return { Icono: ErrorOutlineIcon, color: "#d32f2f", texto: "No enviado" };
    if (read_at) return { Icono: DoneAllIcon, color: "#34B7F1", texto: "Leído" };
    if (delivered_at) return { Icono: DoneAllIcon, color: "#8696A0", texto: "Entregado" };
    if (sent_at) return { Icono: DoneIcon, color: "#8696A0", texto: "Enviado" };
    return { Icono: ScheduleIcon, color: "#8696A0", texto: "Enviando" };
};

/**
 * Configuración visual por source/direction:
 *
 * inbound   → gris,     izquierda
 * bot       → azul,     derecha,   icono 🤖
 * ai        → violeta,  derecha,   icono ✨
 * human     → verde,    derecha,   icono 👤
 * freshdesk → naranja,  derecha,   icono 🎫
 * outbound (sin source) → azul claro, derecha
 */
const BUBBLE_CONFIG = {
    inbound: {
        bg: "#f0f0f0",
        color: "#222",
        align: "flex-start",
        label: null,
        labelColor: "default",
        icon: null,
    },
    bot: {
        bg: "#e3f2fd",
        color: "#0d47a1",
        align: "flex-end",
        label: "Bot",
        labelColor: "#1565c0",
        icon: <SmartToyOutlinedIcon sx={{ fontSize: 13 }} />,
    },
    ai: {
        bg: "#ede7f6",
        color: "#4a148c",
        align: "flex-end",
        label: "IA",
        labelColor: "#7b1fa2",
        icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 13 }} />,
    },
    human: {
        bg: "#e8f5e9",
        color: "#1b5e20",
        align: "flex-end",
        label: "Humano",
        labelColor: "#2e7d32",
        icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 13 }} />,
    },
    freshdesk: {
        bg: "#fff3e0",
        color: "#e65100",
        align: "flex-end",
        label: "Freshdesk",
        labelColor: "#e65100",
        icon: <ConfirmationNumberOutlinedIcon sx={{ fontSize: 13 }} />,
    },
    outbound: {
        bg: "#e3f2fd",
        color: "#0d47a1",
        align: "flex-end",
        label: "Outbound",
        labelColor: "#1565c0",
        icon: <SmartToyOutlinedIcon sx={{ fontSize: 13 }} />,
    },
};

export default function MessageBubble({ message }) {
    const { direction, body, source, created_at, media_url, media_content_type } = message;
    // El archivo original, guardado en Haulers: la nota de voz (el texto de abajo es su
    // transcripción), una foto o sticker, un video o un documento.
    const tipoArchivo = String(media_content_type || "");
    const esAudio = Boolean(media_url) && tipoArchivo.startsWith("audio/");
    const esImagen = Boolean(media_url) && tipoArchivo.startsWith("image/");
    const esVideo = Boolean(media_url) && tipoArchivo.startsWith("video/");
    const esOtroArchivo = Boolean(media_url) && !esAudio && !esImagen && !esVideo;

    // Determinar configuración visual
    let configKey;
    if (direction === "inbound") {
        configKey = "inbound";
    } else if (source && BUBBLE_CONFIG[source]) {
        configKey = source;
    } else {
        configKey = "outbound";
    }

    const cfg = BUBBLE_CONFIG[configKey];
    const timeLabel = hora(created_at);
    const estado = estadoDeEnvio(message);

    // Al pasar el mouse por la hora: el día completo y, si lo mandamos nosotros, cuándo llegó y
    // cuándo lo leyeron.
    const detalle = [
        fechaHora(created_at),
        message.delivered_at && `Entregado: ${fechaHora(message.delivered_at)}`,
        message.read_at && `Leído: ${fechaHora(message.read_at)}`,
        message.failed_at && `No enviado: ${fechaHora(message.failed_at)}`,
    ].filter(Boolean);

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: cfg.align,
                mb: 0.5,
                px: 1,
            }}
        >
            <Box
                sx={{
                    maxWidth: "75%",
                    bgcolor: cfg.bg,
                    color: cfg.color,
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.8,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                }}
            >
                {/* Badge de source para mensajes outbound */}
                {cfg.label && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mb: 0.3 }}>
                        {cfg.icon}
                        <Typography
                            variant="caption"
                            sx={{ fontSize: 10, fontWeight: 700, color: cfg.labelColor, textTransform: "uppercase" }}
                        >
                            {cfg.label}
                        </Typography>
                        {/* Abrió la conversación con una plantilla aprobada (ver TemplateMessageDialog) */}
                        {message.message_type === "template" && (
                            <Typography variant="caption" sx={{ fontSize: 10, color: cfg.labelColor, opacity: 0.7 }}>
                                · Plantilla
                            </Typography>
                        )}
                    </Box>
                )}

                {esAudio && (
                    <Box
                        component="audio"
                        controls
                        preload="none"
                        src={media_url}
                        sx={{ display: "block", width: 260, maxWidth: "100%", height: 36, mb: body ? 0.5 : 0 }}
                    />
                )}

                {esImagen && (
                    <Box component="a" href={media_url} target="_blank" rel="noopener" sx={{ display: "block", mb: 0.5 }}>
                        <Box
                            component="img"
                            src={media_url}
                            alt={body || "Imagen"}
                            sx={{ display: "block", maxWidth: 220, maxHeight: 220, borderRadius: 1.5 }}
                        />
                    </Box>
                )}

                {esVideo && (
                    <Box
                        component="video"
                        controls
                        preload="metadata"
                        src={media_url}
                        sx={{ display: "block", maxWidth: 260, borderRadius: 1.5, mb: 0.5 }}
                    />
                )}

                {esOtroArchivo && (
                    <Box component="a" href={media_url} target="_blank" rel="noopener" sx={{ display: "block", fontSize: 12, mb: 0.5 }}>
                        Abrir archivo
                    </Box>
                )}

                {/* Cuerpo del mensaje */}
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: 13,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        lineHeight: 1.5,
                    }}
                >
                    {body || (esAudio ? "" : "(sin contenido)")}
                </Typography>

                {/* El motivo, si WhatsApp no lo entregó */}
                {message.failed_at && message.failure_reason && (
                    <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#d32f2f", fontSize: 11, lineHeight: 1.35 }}>
                        {message.failure_reason}
                    </Typography>
                )}

                {/* Hora y estado de envío */}
                <Tooltip
                    placement="left"
                    title={<Box sx={{ whiteSpace: "pre-line" }}>{detalle.join("\n")}</Box>}
                >
                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.4, mt: 0.3 }}>
                        <Typography variant="caption" sx={{ opacity: 0.55, fontSize: 10 }}>
                            {timeLabel}
                        </Typography>
                        {estado && (
                            <estado.Icono titleAccess={estado.texto} sx={{ fontSize: 14, color: estado.color }} />
                        )}
                    </Box>
                </Tooltip>
            </Box>
        </Box>
    );
}
